// Turns what the visitor entered (name, colours, business type, mood) into many different, well-formed logo designs.
// The visitor's colours are always kept; only the way they are used changes.
import { DEFAULT_CONFIG } from "./engine";
import { INDUSTRIES, detectIndustry } from "./icons";
import { pairedTextFont } from "./fonts";

export const MOODS = {
  modern: {
    label: "Modern", fonts: ["montserrat", "poppins", "raleway", "grotesk"], containers: ["circle", "rounded", "squircle", "hexagon", "none", "none"], cases: ["upper", "title", "upper"],
    tracking: [20, 120], layouts: ["stacked", "stacked", "horizontal", "horizontal", "horizontal", "emblem", "wordmark", "horizontalRight"], effects: ["none", "none", "none", "shadow", "gloss", "inline"], decor: ["none", "none", "rule", "bar"],
  },
  minimal: {
    label: "Minimal", fonts: ["raleway", "josefin", "poppins", "lora"], containers: ["none", "none", "circle", "rounded"], cases: ["lower", "title", "upper"],
    tracking: [0, 160], layouts: ["stacked", "horizontal", "horizontal", "wordmark", "wordmark", "stacked"], effects: ["none", "none", "none"], decor: ["none", "none", "bar", "rule"],
  },
  bold: {
    label: "Bold", fonts: ["oswald", "bebas", "montserrat", "righteous", "abril"], containers: ["shield", "hexagon", "rounded", "octagon", "badge"], cases: ["upper"],
    tracking: [20, 100], layouts: ["horizontal", "stacked", "horizontal", "emblem", "stacked", "horizontalRight"], effects: ["none", "3d", "gloss", "shadow", "none"], decor: ["none", "bar", "none"],
  },
  elegant: {
    label: "Elegant", fonts: ["playfair", "cinzel", "lora", "dancing", "josefin"], containers: ["circle", "diamond", "arch", "none", "none"], cases: ["upper", "title"],
    tracking: [60, 220], layouts: ["stacked", "stacked", "emblem", "horizontal", "wordmark"], effects: ["none", "none", "inline"], decor: ["rule", "flank", "none", "bar"],
  },
  luxury: {
    label: "Luxury", fonts: ["cinzel", "playfair", "abril", "josefin"], containers: ["diamond", "shield", "badge", "circle", "arch", "none"], cases: ["upper"],
    tracking: [120, 280], layouts: ["stacked", "emblem", "stacked", "wordmark", "horizontal"], effects: ["none", "inline", "gloss", "none"], decor: ["rule", "flank", "rule", "none"],
  },
  playful: {
    label: "Playful", fonts: ["pacifico", "righteous", "poppins", "dancing", "montserrat"], containers: ["circle", "badge", "squircle", "rounded"], cases: ["title", "lower", "title"],
    tracking: [0, 60], layouts: ["stacked", "horizontal", "stacked", "emblem", "horizontal"], effects: ["none", "shadow", "3d", "gloss"], decor: ["none", "none", "bar"],
  },
  tech: {
    label: "Tech", fonts: ["grotesk", "montserrat", "righteous", "oswald", "raleway"], containers: ["hexagon", "rounded", "octagon", "none", "squircle", "none"], cases: ["upper", "lower", "title"],
    tracking: [20, 140], layouts: ["horizontal", "horizontal", "stacked", "wordmark", "horizontal", "emblem"], effects: ["none", "gloss", "shadow", "inline", "none"], decor: ["none", "bar", "none"],
  },
  vintage: {
    label: "Vintage", fonts: ["oswald", "bebas", "josefin", "playfair", "abril", "lora"], containers: ["badge", "circle", "arch", "shield", "octagon"], cases: ["upper"],
    tracking: [60, 200], layouts: ["emblem", "emblem", "stacked", "emblem", "horizontal"], effects: ["none", "none", "inline"], decor: ["flank", "rule", "none"],
  },
};
export const MOOD_IDS = Object.keys(MOODS);

export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pick = (rng, list) => list[Math.floor(rng() * list.length)];
const between = (rng, [lo, hi]) => Math.round(lo + rng() * (hi - lo));

// One design for the brand in `base` (name, tagline, colours, industry, mood). `lock` keeps parts of the base as they are.
export function randomDesign(base, rng, lock = {}) {
  const mood = MOODS[base.mood] || MOODS.modern;
  const industry = INDUSTRIES[base.industry] || INDUSTRIES.general;
  const cfg = { ...DEFAULT_CONFIG, ...base, colors: { ...DEFAULT_CONFIG.colors, ...base.colors } };
  const words = String(base.name || "").trim().split(/\s+/).filter(Boolean);

  if (!lock.layout) cfg.layout = pick(rng, mood.layouts);
  cfg.source = "icon";
  if (cfg.layout !== "wordmark" && rng() < 0.2) cfg.source = "letters";
  if (!lock.icon) {
    const pool = rng() < 0.82 ? industry.icons : INDUSTRIES.general.icons;
    cfg.icon = pick(rng, pool);
  }
  if (!lock.font) cfg.font = pick(rng, mood.fonts);
  cfg.textFont = pairedTextFont(cfg.font);
  cfg.nameCase = pick(rng, mood.cases);
  if (["pacifico", "dancing"].includes(cfg.font)) cfg.nameCase = "title";
  cfg.tracking = between(rng, mood.tracking);
  if (["pacifico", "dancing"].includes(cfg.font)) cfg.tracking = 0;
  cfg.taglineTracking = between(rng, [180, 380]);
  cfg.taglineCase = cfg.font === "pacifico" || cfg.font === "dancing" ? "title" : "upper";

  let containers = mood.containers;
  if (cfg.layout === "emblem") containers = ["circle", "badge", "octagon", "hexagon", "shield"];
  cfg.container = pick(rng, containers);
  const styles = ["solid", "solid", "solid", "gradient", "gradient", "outline"];
  cfg.containerStyle = pick(rng, styles);
  const sameColours = cfg.colors.primary.toLowerCase() === cfg.colors.secondary.toLowerCase();
  if (sameColours && cfg.containerStyle === "gradient") cfg.containerStyle = "solid";
  if (cfg.container === "none" && cfg.containerStyle === "outline") cfg.containerStyle = "solid";

  const tones = ["dark", "dark", "primary", "gradient"];
  if (words.length > 1) tones.push("twoTone", "twoTone");
  cfg.nameTone = pick(rng, tones);
  if (sameColours && (cfg.nameTone === "twoTone" || cfg.nameTone === "gradient")) cfg.nameTone = "dark";

  cfg.effect = pick(rng, mood.effects);
  if (cfg.container === "none" && ["3d", "gloss", "inline"].includes(cfg.effect)) cfg.effect = "none";
  if (cfg.containerStyle === "outline" && ["3d", "gloss"].includes(cfg.effect)) cfg.effect = "none";
  cfg.decor = base.tagline ? pick(rng, mood.decor) : (pick(rng, mood.decor) === "bar" ? "bar" : "none");
  if (cfg.layout === "emblem" || cfg.layout === "iconOnly") cfg.decor = "none";
  cfg.iconScale = Math.round((0.92 + rng() * 0.22) * 100) / 100;
  cfg.nameScale = 1;
  cfg.taglineScale = 1;
  cfg.justify = true;
  cfg.nameLines = "auto";
  cfg.letters = base.letters || "";
  // sometimes the other way round: secondary colour leads
  if (!lock.colors && !sameColours && rng() < 0.22) cfg.colors = { ...cfg.colors, primary: base.colors.secondary, secondary: base.colors.primary };
  return cfg;
}

const signature = (c) => [c.layout, c.source, c.icon, c.container, c.containerStyle, c.font, c.effect, c.nameTone, c.nameCase].join("|");

// `count` different designs (no two alike, a good mix of layouts), reproducible from `seed`. The first one is a calm, safe choice.
export function generateDesigns(base, count = 12, seed = 1, lock = {}) {
  const rng = mulberry32(seed * 7919 + 13);
  const seen = new Set();
  const out = [];
  const used = { emblem: 0, letters: 0, wordmark: 0 };
  const caps = { emblem: Math.ceil(count / 4), letters: Math.ceil(count / 4), wordmark: Math.ceil(count / 6) };
  let guard = 0;
  while (out.length < count && guard < count * 40) {
    guard += 1;
    const design = randomDesign(base, rng, lock);
    const key = signature(design);
    if (seen.has(key)) continue;
    const kinds = [design.layout === "emblem" && "emblem", design.layout === "wordmark" && "wordmark", design.source === "letters" && design.layout !== "wordmark" && "letters"].filter(Boolean);
    if (kinds.some((k) => used[k] >= caps[k])) continue;
    kinds.forEach((k) => { used[k] += 1; });
    seen.add(key);
    out.push(design);
  }
  // the first design shown is a plain icon logo, so the editor opens on something clear
  const first = out.findIndex((d) => d.source === "icon" && (d.layout === "stacked" || d.layout === "horizontal"));
  if (first > 0) out.unshift(out.splice(first, 1)[0]);
  return out;
}

// The brand details the visitor typed, completed: industry from the words used, the industry's usual mood when none was chosen.
export function brandFrom(form) {
  const industry = form.industry && form.industry !== "auto" ? form.industry : detectIndustry(form.name, form.tagline, form.keywords);
  return {
    ...DEFAULT_CONFIG,
    name: form.name.trim(),
    tagline: (form.tagline || "").trim(),
    industry,
    mood: form.mood && form.mood !== "auto" ? form.mood : (INDUSTRIES[industry]?.mood || "modern"),
    colors: { ...form.colors },
  };
}
